import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ToastItem {
  id: number;
  title?: string;
  description?: string;
  variant?: "default" | "success" | "destructive";
  durationMs?: number;
}

interface ToastContextValue {
  push: (t: Omit<ToastItem, "id">) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const idRef = React.useRef(1);

  const push = React.useCallback((t: Omit<ToastItem, "id">) => {
    const id = idRef.current++;
    setItems((prev) => [...prev, { id, ...t }]);
  }, []);

  const remove = (id: number) => setItems((prev) => prev.filter((x) => x.id !== id));

  return (
    <ToastContext.Provider value={{ push }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            duration={item.durationMs ?? 4000}
            onOpenChange={(open) => !open && remove(item.id)}
            className={cn(
              "rounded-md border p-3 pr-8 shadow-lg data-[state=open]:animate-slide-in-right",
              "bg-card text-card-foreground border-border",
              item.variant === "destructive" && "border-destructive/40 bg-destructive/10 text-destructive",
              item.variant === "success" && "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
            )}
          >
            {item.title && <ToastPrimitive.Title className="text-sm font-semibold">{item.title}</ToastPrimitive.Title>}
            {item.description && (
              <ToastPrimitive.Description className="mt-1 text-xs opacity-90 break-all">
                {item.description}
              </ToastPrimitive.Description>
            )}
            <ToastPrimitive.Close className="absolute right-2 top-2 opacity-70 hover:opacity-100">
              <X className="h-3.5 w-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[100] m-4 flex w-96 max-w-[100vw] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
